import { type IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

export interface IAutoConfirmRequest {
  favorites: boolean;
  eligibleProfessionals: boolean;
}

/**
 * Resolves the auto-confirm selection the request will actually carry.
 *
 * `buildJobPayloads` prefers a day's override over the form-level value, so a single
 * overridden day is enough for the payload to ask for auto-confirm even when the
 * form-level toggles read off. Callers that need to reason about what was *sent*
 * (analytics, error attribution) must therefore look at the overrides too.
 */
export const resolveAutoConfirmRequest = (
  formValues: IShiftDetailsFormValues,
): IAutoConfirmRequest => {
  const overrides = Object.values(formValues.shiftOverrides ?? {});

  return {
    favorites: Boolean(
      formValues.autoConfirm?.favorites ||
        overrides.some((override) => override.autoConfirm?.favorites),
    ),
    eligibleProfessionals: Boolean(
      formValues.autoConfirm?.eligibleProfessionals ||
        overrides.some((override) => override.autoConfirm?.eligibleProfessionals),
    ),
  };
};

/** Whether the request asks for auto-confirm at all, on any day. */
export const hasAutoConfirmRequest = (formValues: IShiftDetailsFormValues): boolean => {
  const { favorites, eligibleProfessionals } = resolveAutoConfirmRequest(formValues);

  return favorites || eligibleProfessionals;
};

/** Both toggles off. A fresh object per call — form state must never share a reference. */
const autoConfirmOff = (): IAutoConfirmRequest => ({
  favorites: false,
  eligibleProfessionals: false,
});

/**
 * Strips every Auto-Confirm request from the form values — form-level and per-day override
 * alike (GOTU-7076).
 *
 * Each screen's payment lock only reaches the values that screen owns, so it cannot clear what
 * another screen left behind. Stripping once at the payload boundary covers all of them.
 */
export const clearAutoConfirm = (
  formValues: IShiftDetailsFormValues,
): IShiftDetailsFormValues => {
  const overrides = formValues.shiftOverrides;

  return {
    ...formValues,
    autoConfirm: autoConfirmOff(),
    // Older drafts may not carry the map at all; leave it as it came rather than inventing one.
    ...(overrides && {
      shiftOverrides: Object.fromEntries(
        Object.entries(overrides).map(([dateKey, override]) => [
          dateKey,
          { ...override, autoConfirm: autoConfirmOff() },
        ]),
      ),
    }),
  };
};
